/**
 * @file This files contains function programming utilities.
 */
import { Maybe } from '@codegrade/maybe';
import { AssertionError } from './assert';
/**
 * A `if` statement, but then as an expression.
 *
 * @param cond The condition to check.
 * @param then The function to execute if `cond` is `true`.
 * @param else_ The function to execute if `cond` is `false`.
 * @returns The value returned by `then` or `else_`.
 */
export function ifExpr(cond, then, else_) {
    if (cond) {
        return then();
    }
    else {
        return else_();
    }
}
/**
 * Do pattern matching over a discriminated union.
 *
 * @param data The data to match over.
 * @param tag The tag to dispatch on.
 * @param matchers A mapping between tag value and the function that should be
 *     called when data with this tag is encountered.
 * @returns The value returned by the `matcher` for the given data.
 */
export function match(data, tag, matchers) {
    const matcher = matchers[data[tag]];
    if (matcher == null) {
        throw new AssertionError(`The tag ${data[tag]} should never exist for ${tag}`);
    }
    return matcher(data);
}
/**
 * A wrapper around {@link match} which allows you to separate the passing of
 * the data + tag and the matchers.
 *
 * The reason for this function is that helps typescript to infer the wanted
 * return type of the matchers.
 *
 * @param data The data to make a tag matcher for.
 * @param tag The tag to match on.
 * @returns A object that can be used for matching.
 */
export function makeMatcher(data, tag) {
    return { match: matchers => match(data, tag, matchers) };
}
/**
 * Find a true case and return its result.
 *
 * @param cases A list of 2-tuples, where the first element is a condition
 * function. If this returns `true` the second element will be called and
 * returned as result.
 * @returns A `Just` of the result for the first case for which the condition
 * returned `true`. `Nothing` if no result returned `true`.
 */
export function caseExpr(...cases) {
    for (const [cond, result] of cases) {
        if (cond()) {
            return Maybe.of(result());
        }
    }
    return Maybe.empty();
}
/**
 * Swap the arguments to a functions.
 *
 * @param func A function with two arguments.
 * @returns The given function but now the first argument is the second argument
 * and vice versa.
 */
export function swap(func) {
    return (arg2, arg1) => func(arg1, arg2);
}
/**
 * Curry a function of two arguments.
 *
 * WARNING: This does **not** work correctly for generic functions! It doesn't preserve the types.
 *
 * @param func The function of two arguments to curry.
 * @returns The curried version of `func`.
 */
export function curry2(func) {
    // eslint-disable-next-line jsdoc/require-jsdoc
    return a => b => func(a, b);
}
/**
 * Do a switch as an expression.
 *
 * This is basically the same as `matchers[key]()` but with some extra error handling.
 *
 * You shouldn't use this for tagged unions, use {@link match} instead.
 *
 * @param key The key over which to switch.
 * @param matchers A mapping between switch value and the function that should be
 *     called when this data is passed in to the switch.
 * @returns The value returned by the `matcher` for the given key.
 */
export function switcher(key, matchers) {
    const matcher = matchers[key];
    if (matcher == null) {
        const avail = Object.keys(matchers).join(', ');
        throw new AssertionError(`The key ${key} should never exist, allowed values: ${avail}`);
    }
    return matcher();
}
